// Crosshair schema. Pure types + constants: no DOM, no imports.
//
// The data model is deliberately flat where it helps the editor: every layer
// carries a `params` bag whose keys are defined per shape by `SHAPE_DEFS`
// (see `shapes.ts`), so switching shapes keeps compatible values and the UI can
// be generated from a single table.

export const MAX_LAYERS = 5;
export const MAX_FX = 8;
export const MAX_SLOTS = 24;
export const CONFIG_VERSION = 1;

export type ShapeId =
  | "cross"
  | "dot"
  | "ring"
  | "line"
  | "polygon"
  | "chevron"
  | "bracket"
  | "box"
  | "ticks"
  | "star";

export type ParamValue = number | boolean | string;

export type BlendMode = "normal" | "additive";

export type Trigger = "hit" | "kill" | "damaged" | "fire" | "jump" | "land";

export type Easing = "linear" | "out" | "in" | "inout" | "back" | "elastic";

export type Retrigger = "reset" | "stack" | "ignore";

export interface Outline {
  color: string;
  thickness: number;
  /**
   * `scale` follows the layer (including animation scale); `persistent` keeps
   * the outline's visual thickness while the layer pulses.
   */
  mode: "scale" | "persistent";
}

export interface Fx {
  id: string;
  trigger: Trigger;
  attack: number;
  hold: number;
  release: number;
  easing: Easing;
  retrigger: Retrigger;
  /** Deltas at full envelope. 0 = no change. */
  scale: number;
  rotate: number;
  gap: number;
  thickness: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  /** Fill colour override, or null. */
  color: string | null;
  /** Outline colour override, or null. */
  outlineColor: string | null;
}

export interface Layer {
  id: string;
  shape: ShapeId;
  visible: boolean;
  color: string;
  opacity: number;
  blend: BlendMode;
  offset: { x: number; y: number };
  rotation: number;
  scale: number;
  outline: Outline;
  params: Record<string, ParamValue>;
  fx: Fx[];
}

export interface CrosshairConfig {
  version: 1;
  /** Global multiplier for all layers (e.g. 1080p → 1440p). */
  scale: number;
  /** Snap axis-aligned strokes to the pixel grid. */
  crisp: boolean;
  hideWhenDead: boolean;
  layers: Layer[];
}

export interface CrosshairSlot {
  id: string;
  name: string;
  config: CrosshairConfig;
}

export interface CrosshairStore {
  version: 1;
  slots: CrosshairSlot[];
  activeId: string;
  defaultId: string;
}

export const TRIGGERS: Trigger[] = ["hit", "kill", "damaged", "fire", "jump", "land"];
export const EASINGS: Easing[] = ["linear", "out", "in", "inout", "back", "elastic"];
export const RETRIGGERS: Retrigger[] = ["reset", "stack", "ignore"];

const HEX_RE = /^#[0-9a-f]{6}$/i;
export const isHex = (v: unknown): v is string => typeof v === "string" && HEX_RE.test(v);

export function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/** Stable-ish id for layers/fx/slots (no crypto requirement). */
export function newId(prefix: string): string {
  return `${prefix}${Math.random().toString(36).slice(2, 9)}`;
}

/** Mix two #rrggbb colours in sRGB, t = 0 → a, 1 → b. */
export function mixHex(a: string, b: string, t: number): string {
  const ai = parseInt(a.slice(1), 16);
  const bi = parseInt(b.slice(1), 16);
  const k = clamp(t, 0, 1);
  const ar = (ai >> 16) & 255;
  const ag = (ai >> 8) & 255;
  const ab = ai & 255;
  const br = (bi >> 16) & 255;
  const bg = (bi >> 8) & 255;
  const bb = bi & 255;
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, "0")}`;
}
